/** @jsx jsx */
import React, { useRef, useState } from 'react'
import { animated, ReactSpringHook, useChain, useSpring, useTransition } from 'react-spring'
import { useHarmonicIntervalFn } from 'react-use'
import { jsx, Box } from 'theme-ui'
import { PlayingCardOption } from '../../sharedBetweenServerAndClient__DO_NOT_EDIT/types'
import { PlayingCardsWithAnimatedBack } from '../../components/PlayingCardsWithAnimatedBack'
import { PlayingCard, PLAYING_CARD_HEIGHT_WIDTH_RATIO } from '../../components/PlayingCard'
import { IPositioningMeasurements } from './positionProps'
import { ButtonBlock } from '../../components/ButtonBlock'
import { Modal } from '../../components/Modal'
import { Heading } from '../../components'
const useFade = () => {
  const fadeRef = useRef<ReactSpringHook>(null)
  const fadeAnimation = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    ref: fadeRef,
  })
  return { fadeRef, fadeAnimation }
}

const useFlip = ({ left, width }: { left: number; width: number }) => {
  const spacing = width > 40 ? 10 : 5
  const flipRef = useRef<ReactSpringHook>(null)
  const flipAnimation = useSpring({
    from: { opacity: 0, left: -width - spacing, transform: `perspective(80px) rotateY(${0}deg)` },
    to: {
      opacity: 1,
      left: left,
      transform: `perspective(80px) rotateY(${180}deg)`,
    },
    config: { mass: 5, tension: 500, friction: 80 },
    ref: flipRef,
  })
  return { flipRef, flipAnimation }
}

export const FlippedPlayingCard: React.SFC<{
  card: PlayingCardOption
  left: number
  width: number
  height?: number
}> = ({ card, width, height, left }) => {
  const { fadeRef, fadeAnimation } = useFade()
  const { flipRef, flipAnimation } = useFlip({ left, width })
  useChain([fadeRef, flipRef])
  return (
    <animated.div
      style={{
        // @ts-ignore
        left: flipAnimation.left,
        position: 'absolute',
      }}
    >
      <animated.div style={fadeAnimation}>
        <PlayingCardsWithAnimatedBack
          frontStyle={{
            // @ts-ignore
            opacity: flipAnimation.opacity,
            // @ts-ignore
            transform: flipAnimation.transform.interpolate((t) => `${t} rotateY(180deg)`),
          }}
          backStyle={{
            // @ts-ignore
            opacity: flipAnimation.opacity.interpolate((o) => 1 - o),
            // @ts-ignore
            transform: flipAnimation.transform,
          }}
          {...{ card, width, height }}
        />
      </animated.div>
    </animated.div>
  )
}

const useMockCardsBeingDealt = (cards: PlayingCardOption[]) => {
  const [dealerIndex, setDealerIndex] = useState(0)
  useHarmonicIntervalFn(() => {
    setDealerIndex(dealerIndex === 3 ? 0 : dealerIndex + 1)
  }, 2000)
  switch (dealerIndex) {
    case 0:
      return []
    case 1:
      return cards.slice(0, 3)
    case 2:
      return cards.slice(0, 4)
    case 3:
      return cards.slice(0, 5)
  }
  throw new Error('useMockCardsBeingDealt')
}

export const PlayingCardsBeingDealt: React.SFC<{
  cards: PlayingCardOption[]
  measurements: IPositioningMeasurements
}> = ({ cards, measurements, ...props }) => {
  const [hasModalOpen, setHasModalOpen] = useState(false)
  const spacing = measurements.playingCardWidth > 40 ? 10 : 5
  const usedCards = cards
  return (
    <React.Fragment>
      <Modal
        label="Current Cards"
        isOpen={hasModalOpen}
        onClose={() => setHasModalOpen(false)}
        header={<Heading as="h3">Current Cards</Heading>}
      >
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          {usedCards.map((card, index) => (
            <PlayingCard
              sx={{ ml: index ? 3 : 0 }}
              width={measurements.playingCardWidth}
              key={index}
              card={card}
            />
          ))}
        </Box>
      </Modal>
      <Box
        role="button"
        onClick={() => setHasModalOpen(true)}
        sx={{
          mx: 'auto',
          position: 'relative',
          width: measurements.playingCardWidth * 5 + spacing * 4,
          height: measurements.playingCardWidth * PLAYING_CARD_HEIGHT_WIDTH_RATIO,
        }}
      >
        {usedCards.map((card, index) => (
          <FlippedPlayingCard
            width={measurements.playingCardWidth}
            left={index * (measurements.playingCardWidth + spacing)}
            key={index}
            card={card}
          />
        ))}
      </Box>
    </React.Fragment>
  )
}
